import { Button, Modal, Spin } from 'antd';
import React from 'react';
import ReplaceStrings from '../../../config/replaceStrings';
import Routes from '../../../config/routes';
import { DropdownOption } from '../../../core/models/DropdownOption';
import {
    getListOfDeletedLineupNumberWithEvent,
    lineupNumberHasEvents,
} from '../../../helpers/EventHelper';
import {
    appendMatchPlayersMissingFromRoster,
    getTeamPlayersToDropdownOptions,
} from '../../../helpers/FetchAndTransformHelpers';
import { errorWithUserMessage, popupError, success } from '../../../helpers/NotificationHelper';
import {
    ApiException,
    CreateMatchCoachCommandOfMatchCoach,
    CreateMatchOfficialCommandOfMatchOfficial,
    CreateMatchPlayerCommandOfMatchPlayer,
    EventsClient,
    EventVm,
    MatchDetailVm,
    MatchesClient,
    MatchPlayerVm,
    PlayerPositionEnum,
    UpdateMatchLogCommand,
} from '../../../utils/api';
import MobileShell from '../MobileShell';
import RosterTeamEditor from './components/RosterTeamEditor';
import { Props } from './index';

/** Default goalkeeper slots, matching the desktop match log. */
const DEFAULT_GK_SLOTS = [1, 13];

interface State {
    loading: boolean;
    saving: boolean;
    match?: MatchDetailVm;
    events: EventVm[];
    homeRoster: DropdownOption[];
    awayRoster: DropdownOption[];
    homeLineup: CreateMatchPlayerCommandOfMatchPlayer[];
    awayLineup: CreateMatchPlayerCommandOfMatchPlayer[];
    homeGoalkeepers: number[];
    awayGoalkeepers: number[];
}

class MobileMatchLog extends React.Component<Props, State> {
    private isMounted_ = false;

    state: State = {
        loading: true,
        saving: false,
        events: [],
        homeRoster: [],
        awayRoster: [],
        homeLineup: [],
        awayLineup: [],
        homeGoalkeepers: DEFAULT_GK_SLOTS,
        awayGoalkeepers: DEFAULT_GK_SLOTS,
    };

    componentDidMount(): void {
        this.isMounted_ = true;
        this.load();
    }

    componentWillUnmount(): void {
        this.isMounted_ = false;
    }

    private get matchId(): number {
        return parseInt(this.props.match.params.matchId, 10);
    }

    /** Re-stamps each lineup player's positionId based on the goalkeeper slots. */
    private applyGoalkeeperSlots = (
        lineup: CreateMatchPlayerCommandOfMatchPlayer[],
        goalkeeperSlots: number[]
    ): CreateMatchPlayerCommandOfMatchPlayer[] =>
        lineup.map((p) => {
            const newPos = goalkeeperSlots.includes(p.number)
                ? PlayerPositionEnum.Goalkeeper
                : PlayerPositionEnum.Driver;
            if (p.positionId === newPos) return p;
            return new CreateMatchPlayerCommandOfMatchPlayer({ ...p.toJSON(), positionId: newPos });
        });

    private load = async (): Promise<void> => {
        try {
            const matchClient = new MatchesClient();
            const match = await matchClient.getById(this.matchId);
            const [events, homeRoster, awayRoster] = await Promise.all([
                new EventsClient().getByMatchId(this.matchId),
                getTeamPlayersToDropdownOptions(match.homeTeamId),
                getTeamPlayersToDropdownOptions(match.awayTeamId),
            ]);
            if (!this.isMounted_) return;

            const players = match.players || [];
            const homePlayers = players.filter((p) => p.teamId === match.homeTeamId);
            const awayPlayers = players.filter((p) => p.teamId === match.awayTeamId);

            const homeGks = this.deriveGkSlots(homePlayers);
            const awayGks = this.deriveGkSlots(awayPlayers);

            this.setState({
                match,
                events,
                homeRoster: appendMatchPlayersMissingFromRoster(
                    homeRoster.entities,
                    match.players,
                    match.homeTeamId
                ),
                awayRoster: appendMatchPlayersMissingFromRoster(
                    awayRoster.entities,
                    match.players,
                    match.awayTeamId
                ),
                homeLineup: this.applyGoalkeeperSlots(this.toCommands(homePlayers), homeGks),
                awayLineup: this.applyGoalkeeperSlots(this.toCommands(awayPlayers), awayGks),
                homeGoalkeepers: homeGks,
                awayGoalkeepers: awayGks,
                loading: false,
            });
        } catch (error) {
            if (!this.isMounted_) return;
            errorWithUserMessage('Could not load the match.');
            this.setState({ loading: false });
        }
    };

    private toCommands = (players: MatchPlayerVm[]): CreateMatchPlayerCommandOfMatchPlayer[] =>
        players.map(
            (p) =>
                new CreateMatchPlayerCommandOfMatchPlayer({
                    id: p.id || 0,
                    matchId: this.matchId,
                    teamId: p.teamId,
                    playerId: p.playerId,
                    positionId: p.positionId,
                    number: p.number,
                    capNumber: p.capNumber || p.number,
                })
        );

    /** Goalkeeper slot numbers from saved players, falling back to the desktop default. */
    private deriveGkSlots = (players: MatchPlayerVm[]): number[] => {
        const gks = players
            .filter((p) => p.positionId === PlayerPositionEnum.Goalkeeper)
            .map((p) => p.number);
        return gks.length > 0 ? gks : DEFAULT_GK_SLOTS;
    };

    private handleSelect = (side: 'home' | 'away', cap: number, playerId: number): void => {
        const { match, events } = this.state;
        if (!match) return;

        const isHome = side === 'home';
        const lineup = isHome ? this.state.homeLineup : this.state.awayLineup;
        const roster = isHome ? this.state.homeRoster : this.state.awayRoster;
        const goalkeepers = isHome ? this.state.homeGoalkeepers : this.state.awayGoalkeepers;
        const teamId = isHome ? match.homeTeamId : match.awayTeamId;

        const currentOnCap = lineup.find((p) => p.number === cap);
        if (
            currentOnCap &&
            currentOnCap.playerId !== playerId &&
            lineupNumberHasEvents(events, cap, teamId)
        ) {
            popupError(
                `This ${
                    isHome ? 'home' : 'away'
                } team player cannot be deleted because he is referenced by an event!`
            );
            if (isHome) this.setState({ homeLineup: [...lineup] });
            else this.setState({ awayLineup: [...lineup] });
            return;
        }

        // Preserve an existing match-player id if this player already had a slot.
        const oldValue = lineup.find((p) => p.playerId === playerId);
        const rosterCap = roster.find((p) => p.id === playerId.toString())?.capNumber;

        const newRequest = new CreateMatchPlayerCommandOfMatchPlayer({
            id: oldValue?.id ?? 0,
            matchId: match.id,
            teamId,
            playerId,
            positionId: goalkeepers.includes(cap)
                ? PlayerPositionEnum.Goalkeeper
                : PlayerPositionEnum.Driver,
            number: cap,
            capNumber: rosterCap || cap,
        });

        // Drop any prior slot holding this player and the current slot, then add.
        const next = lineup.filter((l) => l.number !== cap && l.playerId !== playerId);
        next.push(newRequest);

        if (isHome) this.setState({ homeLineup: next });
        else this.setState({ awayLineup: next });
    };

    private handleClear = (side: 'home' | 'away', cap: number): void => {
        const { match, events } = this.state;
        if (!match) return;

        const isHome = side === 'home';
        const teamId = isHome ? match.homeTeamId : match.awayTeamId;

        if (lineupNumberHasEvents(events, cap, teamId)) {
            popupError(
                `This ${side} team player cannot be deleted because he is referenced by an event!`
            );
            return;
        }

        const lineup = (isHome ? this.state.homeLineup : this.state.awayLineup).filter(
            (l) => l.number !== cap
        );
        if (isHome) this.setState({ homeLineup: lineup });
        else this.setState({ awayLineup: lineup });
    };

    private handleToggleGk = (side: 'home' | 'away', cap: number): void => {
        const isHome = side === 'home';
        const current = isHome ? this.state.homeGoalkeepers : this.state.awayGoalkeepers;
        const lineup = isHome ? this.state.homeLineup : this.state.awayLineup;

        const next = current.includes(cap) ? current.filter((c) => c !== cap) : [...current, cap];

        const updatedLineup = this.applyGoalkeeperSlots(lineup, next);
        if (isHome) this.setState({ homeGoalkeepers: next, homeLineup: updatedLineup });
        else this.setState({ awayGoalkeepers: next, awayLineup: updatedLineup });
    };

    private save = async (): Promise<void> => {
        const { match, homeLineup, awayLineup, homeGoalkeepers, awayGoalkeepers } = this.state;
        if (!match) return;

        const deletedHome = getListOfDeletedLineupNumberWithEvent(
            this.state.events,
            match.homeTeamId,
            homeLineup
        );
        const deletedAway = getListOfDeletedLineupNumberWithEvent(
            this.state.events,
            match.awayTeamId,
            awayLineup
        );
        if (deletedHome.length > 0 || deletedAway.length > 0) {
            const errorMessage = `${
                deletedHome.length > 0
                    ? `The following home team players cannot be deleted because they are referenced by an event: ${deletedHome.join(
                          ', '
                      )}. `
                    : ''
            }${
                deletedAway.length > 0
                    ? `The following away team players cannot be deleted because they are referenced by an event: ${deletedAway.join(
                          ', '
                      )}.`
                    : ''
            }`;
            popupError(errorMessage);
            return;
        }

        // The match-log update does a full replace, so we must echo back the
        // existing coaches/officials or the backend would soft-delete them.
        const matchCoaches = (match.coaches || []).map(
            (c) =>
                new CreateMatchCoachCommandOfMatchCoach({
                    id: c.id || 0,
                    matchId: c.matchId,
                    coachId: c.coachId,
                    teamId: c.teamId,
                    roleId: c.roleId,
                })
        );
        const matchOfficials = (match.officials || []).map(
            (o) =>
                new CreateMatchOfficialCommandOfMatchOfficial({
                    id: o.id || 0,
                    matchId: o.matchId,
                    officialId: o.officialId,
                    roleId: o.roleId,
                })
        );
        const matchPlayers = [
            ...this.applyGoalkeeperSlots(homeLineup, homeGoalkeepers),
            ...this.applyGoalkeeperSlots(awayLineup, awayGoalkeepers),
        ];

        const command = new UpdateMatchLogCommand({
            matchId: this.matchId,
            matchCoaches,
            matchOfficials,
            matchPlayers,
        });

        this.setState({ saving: true });
        try {
            await new MatchesClient().updateMatchLog(this.matchId, command);
            if (!this.isMounted_) return;
            success('Lineups saved.');
            this.setState({ saving: false });
            this.confirmGoalkeepersAndGo();
        } catch (error) {
            if (error instanceof ApiException) {
                errorWithUserMessage(error.response);
            } else {
                errorWithUserMessage('Could not save the lineups.');
            }
            if (this.isMounted_) this.setState({ saving: false });
        }
    };

    /** Desktop-style goalkeeper confirmation before entering the livescore. */
    private confirmGoalkeepersAndGo = (): void => {
        const { match, homeLineup, awayLineup, homeGoalkeepers, awayGoalkeepers } = this.state;
        if (!match) return;

        // Resolve slot numbers to actual cap numbers from the lineup.
        const slotToCap = (
            slots: number[],
            lineup: CreateMatchPlayerCommandOfMatchPlayer[]
        ): string =>
            slots
                .map((slot) => lineup.find((l) => l.number === slot)?.capNumber || slot)
                .sort((a, b) => Number(a) - Number(b))
                .join(', #');

        const homeGkCaps = slotToCap(homeGoalkeepers, homeLineup);
        const awayGkCaps = slotToCap(awayGoalkeepers, awayLineup);

        Modal.confirm({
            title: 'Confirm goalkeepers',
            content: `Confirm white #${homeGkCaps} and blue #${awayGkCaps} are goalkeepers.`,
            okText: 'Confirm',
            cancelText: 'Go back',
            onOk: () => {
                this.props.history.push(
                    Routes.ROUTE_MOBILE_MATCH_REPORT.replace(
                        ReplaceStrings.MATCH_ID,
                        this.matchId.toString()
                    )
                );
            },
        });
    };

    render(): React.ReactNode {
        const {
            loading,
            saving,
            match,
            homeRoster,
            awayRoster,
            homeLineup,
            awayLineup,
            homeGoalkeepers,
            awayGoalkeepers,
        } = this.state;
        const onBack = (): void => this.props.history.push(Routes.ROUTE_MOBILE_MATCHES);

        if (loading || !match) {
            return (
                <MobileShell title="Lineups" onBack={onBack}>
                    <div className="m-center">
                        <Spin />
                    </div>
                </MobileShell>
            );
        }

        return (
            <MobileShell title="Lineups" onBack={onBack}>
                <div className="m-lineups">
                    <p className="m-lineups__hint">
                        Fill cap numbers 1&ndash;16 for each team, then start the livescore. You can
                        come back here any time to update the rosters.
                    </p>

                    <div className="m-lineups__teams">
                        <RosterTeamEditor
                            teamName={match.homeTeamDisplayName ?? ''}
                            side="home"
                            roster={homeRoster}
                            lineup={homeLineup}
                            goalkeepers={homeGoalkeepers}
                            onSelect={(cap, playerId): void =>
                                this.handleSelect('home', cap, playerId)
                            }
                            onClear={(cap): void => this.handleClear('home', cap)}
                            onToggleGk={(cap): void => this.handleToggleGk('home', cap)}
                            isSlotLocked={(cap): boolean =>
                                lineupNumberHasEvents(this.state.events, cap, match.homeTeamId)
                            }
                        />
                        <RosterTeamEditor
                            teamName={match.awayTeamDisplayName ?? ''}
                            side="away"
                            roster={awayRoster}
                            lineup={awayLineup}
                            goalkeepers={awayGoalkeepers}
                            onSelect={(cap, playerId): void =>
                                this.handleSelect('away', cap, playerId)
                            }
                            onClear={(cap): void => this.handleClear('away', cap)}
                            onToggleGk={(cap): void => this.handleToggleGk('away', cap)}
                            isSlotLocked={(cap): boolean =>
                                lineupNumberHasEvents(this.state.events, cap, match.awayTeamId)
                            }
                        />
                    </div>

                    <div className="m-lineups__actions">
                        <Button
                            type="primary"
                            size="large"
                            block
                            loading={saving}
                            onClick={this.save}
                        >
                            Save &amp; go to livescore
                        </Button>
                    </div>
                </div>
            </MobileShell>
        );
    }
}

export default MobileMatchLog;
